import React, { useContext, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import LogoutIcon from '@mui/icons-material/Logout'
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import API from '../../services/API'
import { UserContext } from '../../App'
import { openSignup } from './demoSignupUrl'

// Card list shared between the standalone "Switch user" dialog and step 2 of
// the DemoWelcomeModal. Selecting a persona calls the switch endpoint and
// hard-reloads to '/' so every context re-bootstraps as the new persona.
export const DemoPersonaList = ({
  personas = [],
  currentPersonaKey,
  onCurrentSelected,
}) => {
  const [switchingKey, setSwitchingKey] = useState(null)
  const [error, setError] = useState(false)

  const handleSelect = async (persona) => {
    if (switchingKey) return

    if (persona.key === currentPersonaKey) {
      onCurrentSelected?.()
      return
    }

    setError(false)
    setSwitchingKey(persona.key)

    const res = await API.post(
      '/demo/switch-persona',
      { personaKey: persona.key },
      { skipGlobalErrorHandler: true },
    )

    if (res.status === 200) {
      // Full reload is the simplest way to re-bootstrap every context
      // (user, org config, tenant info) for the new persona.
      window.location.assign('/')
      return
    }

    setSwitchingKey(null)
    setError(true)
  }

  return (
    <Stack spacing={1.5}>
      {error && (
        <Alert severity="error" onClose={() => setError(false)}>
          Sorry, we couldn't switch persona just now. Please try again.
        </Alert>
      )}

      {personas.map((persona) => {
        const isCurrent = persona.key === currentPersonaKey
        const isSwitching = switchingKey === persona.key

        return (
          <Box
            key={persona.key}
            onClick={() => handleSelect(persona)}
            sx={{
              p: 2,
              borderRadius: 2,
              border: '2px solid',
              borderColor: isCurrent ? 'primary.main' : 'divider',
              bgcolor: isCurrent ? 'action.selected' : 'background.paper',
              cursor: switchingKey ? 'default' : 'pointer',
              opacity: switchingKey && !isSwitching ? 0.6 : 1,
              transition: 'border-color 0.15s ease, background-color 0.15s ease',
              '&:hover': switchingKey
                ? {}
                : {
                    borderColor: 'primary.main',
                    bgcolor: 'action.hover',
                  },
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={2}
            >
              <Box>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography fontWeight={800}>{persona.label}</Typography>
                  {isCurrent && (
                    <Chip
                      label="Current"
                      color="primary"
                      size="small"
                      sx={{ borderRadius: 1 }}
                    />
                  )}
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {persona.name}
                </Typography>
                {persona.description && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {persona.description}
                  </Typography>
                )}
              </Box>
              {isSwitching && <CircularProgress size={22} />}
            </Stack>
          </Box>
        )
      })}
    </Stack>
  )
}

const DemoPersonaPicker = ({ open, onClose, personas, currentPersonaKey }) => {
  const { setUser } = useContext(UserContext)

  // In demo mode the avatar menu (and its Log Out item) is replaced by the
  // switch-user pill, so the demo exit lives here. Mirrors AvatarMenu's
  // handleLogout exactly.
  const handleLogout = () => {
    API.delete('/logout')
      .then((res) => {
        setUser({})
        window.location.reload()
      })
      .catch((e) => console.log(e))
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Switch user</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Explore the demo as any of the roles below. Switching takes you to
          that person's home page.
        </Typography>
        <DemoPersonaList
          personas={personas}
          currentPersonaKey={currentPersonaKey}
          onCurrentSelected={onClose}
        />

        {/* Conversion banner — deliberately styled unlike the persona cards
            (success tint, no hover-to-select affordance on the card itself)
            so it can't be mistaken for another demo user. */}
        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'success.main',
            bgcolor: (theme) => alpha(theme.palette.success.main, 0.08),
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            spacing={1.5}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <RocketLaunchIcon color="success" />
              <Box>
                <Typography fontWeight={800}>
                  Ready to use AmbuNet for real?
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Sign up and get started today.
                </Typography>
              </Box>
            </Stack>
            <Button
              variant="contained"
              color="success"
              size="small"
              disableElevation
              endIcon={<ArrowForwardIcon />}
              onClick={openSignup}
              sx={{
                textTransform: 'none',
                fontWeight: 800,
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              Sign up
            </Button>
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          size="small"
          color="inherit"
          startIcon={<LogoutIcon fontSize="small" />}
          onClick={handleLogout}
          sx={{ mr: 'auto', color: 'text.secondary', textTransform: 'none' }}
        >
          Log out of demo
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

export default DemoPersonaPicker
